/**
 * Cart Drawer Recommendations
 * Handles dynamic product recommendations based on cart items
 */

class CartDrawerRecommendations {
  constructor() {
    CartDrawerRecommendations.setupGlobalDragToScroll();
    this.container = document.querySelector('.cart-drawer-recommendations');
    this.productsContainer = this.container?.querySelector('.cart-drawer-recommendations__products');
    this.loadingContainer = this.container?.querySelector('.cart-drawer-recommendations__loading');
    this.fallbackContainer = this.container?.querySelector('.cart-drawer-recommendations__fallback');
    this.sectionId = this.container?.dataset.sectionId;
    this.isInitialized = false;
    this.isLoading = false;
    this.hasLoadedRecommendations = false;
    this.preservedState = null;
    this.lastCartProductIds = ''; // Track previous cart product IDs
    this.cartHandles = []; // Track cart product handles for upgrade/downgrade exclusion rules
    this.pendingCartProductIds = null; // Store product IDs when container doesn't exist
    this.cartUpdateUnsubscriber = null; // For pub/sub cleanup
    this.isQuantityOnlyChange = false; // Flag to track quantity-only changes
    
    // Bind methods to avoid creating new functions on every call
    this.handleVariantChangeBound = this.handleVariantChange.bind(this);
    
    this.init();

    // Note: Event listeners are now handled in the init() method to avoid duplicates
  }
  
  // Map color names to hex values for swatches
  getColorHex(colorName) {
    const colorMap = {
      'Beige': '#B9B0A3',
      'Black': '#000000',
      'Noir': '#000000',
      'White': '#FFFFFF',
      'Blanc': '#FFFFFF',
      'Bronze': 'linear-gradient(180deg, #9E6346 0%, #B8A494 100%)',
      'Brass': 'linear-gradient(180deg, #B6956C 0%, #E8D3B9 100%)',
      'Chrome': 'linear-gradient(180deg, #D9D9D9 0%, #737373 100%)',
      'Gray': '#808080',
      'Grey': '#808080',
      'Gris': '#808080',
      'Blue': 'linear-gradient(180deg, #91ABB7 0%, #DADFE1 100%)',
    };
    
    // Exact match first
    if (colorMap[colorName]) return colorMap[colorName];
    
    // Fallback: check if any key appears as a whole word inside colorName
    // e.g. "Matte Chrome" → matches "Chrome"
    const upperName = colorName.toUpperCase();
    for (const key of Object.keys(colorMap)) {
      const upperKey = key.toUpperCase();
      // Match whole word: surrounded by start/end or non-alpha characters
      const regex = new RegExp(`(^|[^A-Z])${upperKey}([^A-Z]|$)`);
      if (regex.test(upperName)) {
        return colorMap[key];
      }
    }
    
    return '#CCCCCC';
  }

  init() {
    // Prevent multiple initializations
    if (this.isInitialized) return;
    this.isInitialized = true;
    
    // Ensure global drag-to-scroll is active
    CartDrawerRecommendations.setupGlobalDragToScroll();
    
    // Show recommendations when cart drawer opens (only if not already loaded)
    document.addEventListener('cart-drawer:opened', (event) => {
      // Re-find container elements in case they were re-rendered
      this.container = document.querySelector('.cart-drawer-recommendations');
      this.loadingContainer = this.container?.querySelector('.cart-drawer-recommendations__loading');
      this.productsContainer = this.container?.querySelector('.cart-drawer-recommendations__products');
      this.fallbackContainer = this.container?.querySelector('.cart-drawer-recommendations__fallback');
      
      // Check if we have cart product IDs and load recommendations if needed
      let cartProductIds = this.container?.dataset.cartProductIds;
      
      // If no cart product IDs in container but we have pending ones, use those
      if ((!cartProductIds || cartProductIds.trim() === '') && this.pendingCartProductIds) {
        cartProductIds = this.pendingCartProductIds;
        this.container.dataset.cartProductIds = cartProductIds;
        this.pendingCartProductIds = null; // Clear pending IDs
      }
      
      if (this.container && cartProductIds && cartProductIds.trim() !== '') {
        // Initialize the last cart product IDs when drawer opens
        if (!this.lastCartProductIds) {
          this.lastCartProductIds = cartProductIds;
        }
        
        // Ensure container is visible when there are cart items
        this.ensureContainerVisible();
        
        // Check if recommendations are already visible and properly rendered
        const isAlreadyVisible = this.isRecommendationsVisible();
        
        if (isAlreadyVisible) {
          this.addCartButtonListeners();
        } else {
          this.loadRecommendations();
        }
      }
    });

    // Update recommendations when cart changes (only if product composition changes)
    document.addEventListener('cart:updated', (event) => {
      // Reset any stuck buttons first
      this.resetStuckButtons();
      
      if (event.detail && event.detail.cart && event.detail.cart.items && Array.isArray(event.detail.cart.items)) {
        // Check if the product composition has changed (not just quantities)
        const currentProductIds = event.detail.cart.items.map(item => item.product_id).join(',');
        
        if (currentProductIds !== this.lastCartProductIds) {
          // Product composition changed, update recommendations
          this.isQuantityOnlyChange = false;
          this.lastCartProductIds = currentProductIds;
          this.updateCartProductIds(event.detail.cart);
        } else {
          // Only re-initialize event listeners, don't reload recommendations
          setTimeout(() => {
            this.reinitializeListeners();
          }, 100);
        }
      } else {
        // If no cart data or invalid structure, check if container exists and has product IDs
        if (this.container && this.container.dataset.cartProductIds) {
          this.loadRecommendations();
        } else {
          this.hideRecommendations();
        }
      }
    });

    // Also listen to the pub/sub cart update event as a fallback
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source !== 'cart-drawer-recommendations') {
          // Reset any stuck buttons first
          this.resetStuckButtons();
          
          // Check if this is a quantity-only change
          if (event.cartData && event.cartData.items && Array.isArray(event.cartData.items)) {
            const currentProductIds = event.cartData.items.map(item => item.product_id).join(',');
            const isQuantityOnlyChange = currentProductIds === this.lastCartProductIds;
            
            if (isQuantityOnlyChange) {
              this.isQuantityOnlyChange = true;
            } else {
              this.updateCartProductIds(event.cartData.items);
            }
          }
          
          // Re-initialize listeners when cart is updated from other sources
          setTimeout(() => {
            this.reinitializeListeners();
          }, 100);
        }
      });
    }

    // Re-initialize when cart drawer is re-rendered
    document.addEventListener('cart-drawer:rendered', (event) => {
      // Check if we have cart data and update product IDs
      if (event.detail && event.detail.cartData) {
        this.updateCartProductIds(event.detail.cartData);
      }
      
      setTimeout(() => {
        // Reset any stuck buttons first
        this.resetStuckButtons();
        
        // Re-find container elements in case they were re-rendered
        this.container = document.querySelector('.cart-drawer-recommendations');
        this.loadingContainer = this.container?.querySelector('.cart-drawer-recommendations__loading');
        this.productsContainer = this.container?.querySelector('.cart-drawer-recommendations__products');
        this.fallbackContainer = this.container?.querySelector('.cart-drawer-recommendations__fallback');
        
        // Preserve lastCartProductIds if container has cart product IDs
        if (this.container && this.container.dataset.cartProductIds && !this.lastCartProductIds) {
          this.lastCartProductIds = this.container.dataset.cartProductIds;
        }
        
        // Check if recommendations are already visible and properly rendered
        const isAlreadyVisible = this.isRecommendationsVisible();
        
        if (isAlreadyVisible) {
          // Just reinitialize event listeners if recommendations are already visible
          setTimeout(() => {
            this.addCartButtonListeners();
          }, 100);
        } else {
          // Load recommendations if they're not visible and it's not a quantity-only change
          if (this.isQuantityOnlyChange) {
            this.isQuantityOnlyChange = false; // Reset the flag
          } else {
            this.loadRecommendations();
          }
        }
      }, 150);
    });

    // Listen for section updates (for when the cart drawer section is updated)
    document.addEventListener('shopify:section:load', (event) => {
      if (event.detail.sectionId === 'cart-drawer') {
        setTimeout(() => {
          this.reinitialize();
        }, 100);
      }
    });

    // Initial load if cart has items and drawer is already open
    if (this.container?.dataset?.cartProductIds && document.querySelector('cart-drawer')?.classList.contains('active')) {
      // Initialize the last cart product IDs
      this.lastCartProductIds = this.container.dataset.cartProductIds;
      this.loadRecommendations();
    }
  }

  async loadRecommendations() {
    if (!this.container || !this.productsContainer) {
      return;
    }

    // Prevent multiple simultaneous loads
    if (this.isLoading) {
      return;
    }

    const cartProductIds = this.container.dataset.cartProductIds;
    
    if (!cartProductIds) {
      this.hideRecommendations();
      this.lastCartProductIds = '';
      return;
    }

    // Initialize lastCartProductIds if not set
    if (!this.lastCartProductIds) {
      this.lastCartProductIds = cartProductIds;
    }

    // Ensure container is visible and remove any inline display:none
    this.container.style.display = 'block';
    this.container.style.removeProperty('display'); // Remove any inline display styles
    
    this.isLoading = true;
    this.showLoading();

    try {
      // Fetch cart handles for upgrade/downgrade exclusion rules
      const cartJsUrl = (window.routes && window.routes.cart_url ? window.routes.cart_url + '.js' : '/cart.js');
      const cartResponse = await fetch(cartJsUrl);
      if (cartResponse.ok) {
        const cartData = await cartResponse.json();
        this.cartHandles = (cartData.items || []).map(item => item.handle);
      }

      // --- Priority 1: cross-sell products from metafield custom.cross_sell_product ---
      const crossSellHandles = (this.container.dataset.crossSellHandles || '').trim();

      if (crossSellHandles) {
        const handles = crossSellHandles.split(',').map(h => h.trim()).filter(Boolean);

        // Fetch all cross-sell products in parallel via /products/{handle}.js
        const productResults = await Promise.all(
          handles.map(handle =>
            fetch(`/products/${handle}.js`)
              .then(r => r.ok ? r.json() : null)
              .catch(() => null)
          )
        );

        const validProducts = productResults.filter(p => p !== null);

        if (validProducts.length > 0) {
          this.renderRecommendations(validProducts);
          this.hasLoadedRecommendations = true;
          return; // Done – skip Shopify recommendations fallback
        }
      }

      // --- Priority 2: Shopify recommendations API (all cart product IDs, merged) ---
      const allProductIds = cartProductIds.split(',').map(id => id.trim()).filter(Boolean);

      // Fetch Shopify recommendations for each cart product in parallel
      const recsResults = await Promise.all(
        allProductIds.map(id =>
          fetch(`/recommendations/products.json?product_id=${id}&limit=6`)
            .then(r => r.ok ? r.json() : null)
            .catch(() => null)
        )
      );

      // Merge and deduplicate products from all recommendation responses
      const seenIds = new Set();
      const mergedProducts = [];
      for (const result of recsResults) {
        if (!result || !result.products) continue;
        for (const product of result.products) {
          if (!seenIds.has(product.id)) {
            seenIds.add(product.id);
            mergedProducts.push(product);
          }
        }
      }

      if (mergedProducts.length > 0) {
        this.renderRecommendations(mergedProducts);
        this.hasLoadedRecommendations = true;
      } else {
        this.showFallback();
        this.hasLoadedRecommendations = true;
      }
    } catch (error) {
      this.showFallback();
      this.hasLoadedRecommendations = true;
    } finally {
      this.isLoading = false;
    }
  }



  renderRecommendations(products) {
    if (!this.productsContainer) {
      return;
    }

    // Store current recommendations for variant selection
    this.currentRecommendations = products;
    
    // Debug: Log the first product to see its structure
    // if (products.length > 0) {
    //   console.log('CartDrawerRecommendations: First product structure:', products[0]);
    //   console.log('CartDrawerRecommendations: Variants:', products[0].variants);
    // }

    // Filter out products already in cart
    const cartProductIds = this.container.dataset.cartProductIds.split(',');
    
    // Filter out products already in cart AND specific products to exclude
    const filteredProducts = products.filter(product => {
      const isInCart = cartProductIds.includes(product.id.toString());
      const isExcludedProduct = this.isProductExcluded(product.title);
      const isDowngradeExcluded = this.isDowngradeExcluded(product);
      
      return !isInCart && !isExcludedProduct && !isDowngradeExcluded;
    });

    if (filteredProducts.length === 0) {
      this.showFallback();
      return;
    }

    // Create list items for each product (showing first variant only)
    const productItems = [];
    
    filteredProducts.slice(0, this.getRecommendationsLimit()).forEach(product => {
      // Always use the first variant
      const firstVariant = product.variants[0];
      const item = this.renderProductItem(product, firstVariant);
      productItems.push(item);
    });
    
    // Create simple list structure
    const listContainer = document.createElement('div');
    listContainer.className = 'cart-drawer-recommendations-list';
    
    // Create list items
    productItems.forEach((item, index) => {
      const listItem = document.createElement('div');
      listItem.className = 'cart-drawer-recommendations-item';
      listItem.innerHTML = item;
      listContainer.appendChild(listItem);
    });

    // Clear container first
    this.productsContainer.innerHTML = '';
    
    // Append the list container
    this.productsContainer.appendChild(listContainer);
    
    this.hideLoading();
    this.showProducts();
    
    // Add event listeners
    this.addCartButtonListeners();
  }

  // Helper method to check if product has multiple variant options (size, color, etc.)
  hasMultipleVariantOptions(product) {
    if (product.variants.length <= 1) {
      // console.log('CartDrawerRecommendations: Product has only 1 variant:', product.title);
      return false;
    }
    
    // Check if variants have different option values
    const firstVariant = product.variants[0];
    const hasMultipleOptions = product.variants.some(variant => {
      return variant.options.some((option, index) => 
        option !== firstVariant.options[index]
      );
    });
    
    // console.log('CartDrawerRecommendations: Product variants check:', {
    //   title: product.title,
    //   variantCount: product.variants.length,
    //   firstVariantOptions: firstVariant.options,
    //   hasMultipleOptions: hasMultipleOptions
    // });
    
    return hasMultipleOptions;
  }

  // Helper method to get variants to show (limit to avoid overwhelming the user)
  getVariantsToShow(product) {
    const maxVariantsToShow = 3; // Show max 3 variants per product
    
    // Group variants by their first option (usually the main differentiator like size)
    const groupedVariants = {};
    product.variants.forEach(variant => {
      const firstOption = variant.options[0] || 'default';
      if (!groupedVariants[firstOption]) {
        groupedVariants[firstOption] = [];
      }
      groupedVariants[firstOption].push(variant);
    });
    
    // Take one variant from each group, prioritizing available ones
    const variantsToShow = [];
    Object.keys(groupedVariants).forEach(optionKey => {
      const variantsInGroup = groupedVariants[optionKey];
      // Prefer available variants
      const availableVariant = variantsInGroup.find(v => v.available);
      const variantToShow = availableVariant || variantsInGroup[0];
      
      if (variantsToShow.length < maxVariantsToShow) {
        variantsToShow.push(variantToShow);
      }
    });
    
    return variantsToShow;
  }

  // Render variant selector with pills and color swatches for products with multiple variants
  renderVariantSelector(product, selectedVariant) {
    if (product.variants.length <= 1) return '';
    
    // Check if variants actually have different options
    const firstVariant = product.variants[0];
    const hasDifferentOptions = product.variants.some(variant => 
      variant.options.some((option, index) => option !== firstVariant.options[index])
    );
    
    if (!hasDifferentOptions) return '';
    
    let selectorHTML = '';
    
    // Create selectors for each option position (0, 1, 2 for first, second, third options)
    for (let optionIndex = 0; optionIndex < firstVariant.options.length; optionIndex++) {
      // Get the option values from the product.options structure
      const productOption = product.options[optionIndex];
      if (!productOption || !productOption.values || productOption.values.length <= 1) continue;
      
      const optionLabel = this.getOptionName(product, optionIndex);
      const optionValues = productOption.values;
      const isColorOption = optionLabel === 'Color' || optionLabel === 'Couleur' || optionLabel === 'اللون';
      
      if (isColorOption) {
        selectorHTML += `
          <div class="cart-drawer-variant-option">
            <div class="cart-drawer-variant-option__label">${optionLabel}</div>
            <div class="cart-drawer-variant-radios cart-drawer-variant-radios--color" 
                 data-product-id="${product.id}" 
                 data-option-index="${optionIndex}">
              ${optionValues.map(optionValue => {
                const isSelected = optionValue === selectedVariant.options[optionIndex];
                const isAvailable = this.isOptionValueAvailable(product, optionIndex, optionValue);
                const colorHex = this.getColorHex(optionValue);
                const isGradient = colorHex.includes('gradient');
                
                return `
                  <div class="cart-drawer-variant-wrapper ${isSelected ? 'active' : ''} ${!isAvailable ? 'unavailable' : ''}" 
                       data-option-value="${optionValue}">
                    <input 
                      type="radio" 
                      name="cart-drawer-option-${product.id}-${optionIndex}" 
                      value="${optionValue}"
                      id="cart-drawer-${product.id}-${optionIndex}-${optionValue}"
                      ${isSelected ? 'checked' : ''}
                      data-product-id="${product.id}"
                      data-option-index="${optionIndex}"
                    >
                    <label for="cart-drawer-${product.id}-${optionIndex}-${optionValue}"
                           style="${isGradient ? `background: ${colorHex}` : `background-color: ${colorHex}`}">
                    </label>
                    <span class="cart-drawer-option-value">${optionValue}</span>
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        `;
      } else {
        selectorHTML += `
          <div class="cart-drawer-variant-option">
            <div class="cart-drawer-variant-select-container">
              <select name="cart-drawer-option-${product.id}-${optionIndex}" 
                      class="cart-drawer-variant-select" 
                      data-product-id="${product.id}" 
                      data-option-index="${optionIndex}">
                ${optionValues.map(optionValue => {
                  const isSelected = optionValue === selectedVariant.options[optionIndex];
                  const isAvailable = this.isOptionValueAvailable(product, optionIndex, optionValue);
                  return `
                    <option value="${optionValue}" ${isSelected ? 'selected' : ''} ${!isAvailable ? 'disabled' : ''}>
                      ${optionValue}
                    </option>
                  `;
                }).join('')}
              </select>
            </div>
          </div>
        `;
      }
    }
    
    return selectorHTML;
  }
  
  // Check if an option value is available (has at least one available variant)
  isOptionValueAvailable(product, optionIndex, optionValue) {
    return product.variants.some(variant => 
      variant.options[optionIndex] === optionValue && variant.available
    );
  }

  // Check if a product should be excluded from recommendations
  isProductExcluded(productTitle) {
    const excludedProducts = [
      "Shower Filter Refill Capsule",
      "Shower Head Refill Cartridges"
    ];
    
    return excludedProducts.includes(productTitle);
  }

  // Check if a product should be excluded because the customer already has a superior version in their cart.
  // Rules: When Shower Head+ is in the cart, hide the basic Shower Head (downgrade) and all
  //        related accessories/filters that are already bundled with or incompatible with Shower Head+.
  //        Recommending Shower Head+ when Shower Head 2.0 is in the cart is fine (it's an upsell).
  isDowngradeExcluded(product) {
    const handle = product.handle;
    if (!handle || !this.cartHandles || this.cartHandles.length === 0) return false;

    // If the customer already has Shower Head+, exclude these products
    if (this.cartHandles.includes('hello-klean-shower-head-plus')) {
      const excludedWhenPlusInCart = [
        'hello-klean-shower-head',
        'hello-klean-rain-shower',
        'hello-klean-shower-filter',
        'refill-cartridges',
        'refill-capsule',
        'hello-klean-refill-capsule-rain-shower-shower-filter',
        'shower-head-1-year-refill-kit',
        'shower-adapter',
      ];

      if (excludedWhenPlusInCart.includes(handle)) {
        return true;
      }
    }

    return false;
  }

  // Refresh recommendations after adding a product to cart
  async refreshRecommendationsAfterAdd() {
    if (!this.container) return;
    
    // Get current cart state to see what products are now in cart
    try {
      const cartJsUrl = (window.routes && window.routes.cart_url ? window.routes.cart_url + '.js' : '/cart.js');
      const response = await fetch(cartJsUrl);
      if (!response.ok) return;
      
      const cart = await response.json();
      
      // Update cart product IDs with the new cart state
      if (cart.items && cart.items.length > 0) {
        const newCartProductIds = cart.items.map(item => item.product_id).join(',');
        
        // Update the container's data attribute
        this.container.dataset.cartProductIds = newCartProductIds;
        
        // Update our tracking
        this.lastCartProductIds = newCartProductIds;
        
        // Reload recommendations to filter out the newly added product
        this.hasLoadedRecommendations = false;
        this.loadRecommendations();
      }
    } catch (error) {
      console.error('CartDrawerRecommendations: Error refreshing recommendations:', error);
    }
  }

  // Get the actual option name from the product data
  getOptionName(product, optionIndex) {
    if (product.options && product.options[optionIndex]) {
      return product.options[optionIndex].name;
    }
    return `Option ${optionIndex + 1}`;
  }

  renderProductItem(product, variant) {
    const image = product.featured_image || (product.images && product.images[0]);
    const imageUrl = image ? image.replace('.jpg', '_200x200.jpg') : '';
    
    // Generate variant title
    let variantTitle = product.title;
    if (product.variants.length > 1) {
      const variantOptions = variant.options.join(' / ');
      variantTitle = `${product.title} - ${variantOptions}`;
    }
    
    // Generate variant selector if product has multiple variants
    let variantSelector = '';
    if (product.variants.length > 1 && this.hasMultipleVariantOptions(product)) {
      variantSelector = this.renderVariantSelector(product, variant);
    }
    
    return `
      <div class="cart-drawer-product-card">
        <a href="${product.url}">
          <div class="cart-drawer-product-card__image">
            ${imageUrl ? `<img src="${imageUrl}" alt="${variantTitle}" loading="lazy" width="100" height="100">` : ''}
          </div>
        </a>
        <div class="cart-drawer-product-card__info">
          <a href="${product.url}">
            <h4 class="cart-drawer-product-card__title">${product.title}</h4>
          </a>

          <div class="cart-drawer-product-card__price">
            ${this.formatPrice(variant.price, variant.compare_at_price)}
          </div>

          ${variantSelector}

          <form method="post" action="/cart/add" class="cart-drawer-product-form">
            <input type="hidden" name="id" value="${variant.id}">
            <input type="hidden" name="quantity" value="1">
            <button 
              type="submit"
              class="cart-drawer-product-card__add button button--small"
              ${!variant.available ? 'disabled' : ''}
              aria-label="Add ${variantTitle} to cart"
            >
              Add
            </button>
          </form>
        </div>
      </div>
    `;
  }

  formatPrice(price, compareAtPrice) {
    // Use Shopify's built-in money formatting for consistent currency display
    let moneyFormat = window.moneyFormat;
    
    // Try to get the money format from the main product state if available (higher priority)
    if (window.LPV2MainProductInitialState && window.LPV2MainProductInitialState.moneyFormat) {
      moneyFormat = window.LPV2MainProductInitialState.moneyFormat;
    }
    
    // Use Shopify's formatMoney function if available
    if (window.Shopify && window.Shopify.formatMoney && moneyFormat) {
      const formattedPrice = Shopify.formatMoney(price, moneyFormat);
      const formattedComparePrice = compareAtPrice ? Shopify.formatMoney(compareAtPrice, moneyFormat) : null;
      
      if (formattedComparePrice && compareAtPrice > price) {
        return `
          <span class="price price--sale">${formattedPrice}</span>
          <span class="price price--compare">${formattedComparePrice}</span>
        `;
      }
      
      return `<span class="price">${formattedPrice}</span>`;
    }
    
    // Fallback to basic formatting if Shopify.formatMoney is not available
    const formattedPrice = (price / 100).toFixed(2);
    const formattedComparePrice = compareAtPrice ? (compareAtPrice / 100).toFixed(2) : null;
    
    if (formattedComparePrice && compareAtPrice > price) {
      return `
        <span class="price price--sale">${formattedPrice}</span>
        <span class="price price--compare">${formattedComparePrice}</span>
      `;
    }
    
    return `<span class="price">${formattedPrice}</span>`;
  }

  async addToCart(button) {
    const variantId = button.dataset.variantId;
    const productId = button.dataset.productId;
    
    if (!variantId) {
      console.error('CartDrawerRecommendations: No variant ID found');
      return;
    }

    // Disable button and show loading state
    button.disabled = true;
    button.textContent = window.translation.adding;

    try {
      // Get the cart drawer element
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) {
        console.error('CartDrawerRecommendations: Cart drawer not found');
        throw new Error('Cart drawer not found');
      }

      // Use the same approach as product form
      if (typeof fetchConfig === 'undefined') {
        console.error('CartDrawerRecommendations: fetchConfig function is not available');
        throw new Error('fetchConfig function not available');
      }
      
      const config = fetchConfig('javascript');
      
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      
      // Add sections for cart drawer update
      const sectionsToRender = cartDrawer.getSectionsToRender();
      
      // Check if getSectionsToRender method exists and returns valid data
      if (!sectionsToRender || !Array.isArray(sectionsToRender)) {
        console.error('CartDrawerRecommendations: Invalid sections to render', sectionsToRender);
        throw new Error('Invalid sections configuration');
      }
      
      // Convert sections to the correct format for the API
      const sectionIds = sectionsToRender.map((section) => section.id).join(',');
      
      formData.append('sections', sectionIds);
      formData.append('sections_url', window.location.pathname);
      
      config.body = formData;

      const response = await fetch(`${window.routes.cart_add_url}`, config);

      if (!response.ok) {
        console.error('CartDrawerRecommendations: Response not ok', {
          status: response.status,
          statusText: response.statusText
        });
        throw new Error('Failed to add to cart');
      }

      const cartData = await response.json();
      
      // Use the same cart update system as product form
      if (cartData.status) {
        console.error('CartDrawerRecommendations: Cart data has error status', cartData);
        // Handle error
        throw new Error(cartData.description || 'Failed to add to cart');
      }

      // Check if the required sections are available
      if (!cartData.sections || !cartData.sections['cart-drawer']) {
        console.error('CartDrawerRecommendations: Missing cart-drawer section in response', {
          sections: cartData.sections,
          availableSections: cartData.sections ? Object.keys(cartData.sections) : null
        });
        
        // Fallback: trigger a cart refresh instead of trying to render contents
        
        // Use the same approach as product form - publish cart update event
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-drawer-recommendations',
            productVariantId: variantId,
            cartData: cartData,
          });
        } else {
          // Fallback to custom event if pub/sub is not available
          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { cart: cartData }
          }));
        }
        
        return; // Exit early, don't try to render contents
      }

      // Update cart drawer contents using the same method as product form
      
      // Try to update cart drawer with retry mechanism
      let retryCount = 0;
      const maxRetries = 3;
      let cartDrawerUpdated = false;
      
      while (retryCount < maxRetries && !cartDrawerUpdated) {
        try {
          // Re-find the cart drawer element in case it was re-rendered during locale change
          const currentCartDrawer = document.querySelector('cart-drawer');
          if (!currentCartDrawer) {
            console.error(`CartDrawerRecommendations: Cart drawer not found after re-finding (attempt ${retryCount + 1})`);
            retryCount++;
            if (retryCount < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, 100)); // Wait 100ms before retry
              continue;
            }
            throw new Error('Cart drawer not found after re-finding');
          }
          
          if (typeof currentCartDrawer.renderContents !== 'function') {
            console.error(`CartDrawerRecommendations: renderContents method not available on cart drawer (attempt ${retryCount + 1})`);
            retryCount++;
            if (retryCount < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, 100)); // Wait 100ms before retry
              continue;
            }
            throw new Error('Cart drawer renderContents method not available');
          }
          
          currentCartDrawer.renderContents(cartData);
          cartDrawerUpdated = true;
          
        } catch (error) {
          console.error(`CartDrawerRecommendations: Error updating cart drawer (attempt ${retryCount + 1})`, error);
          retryCount++;
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 100)); // Wait 100ms before retry
          } else {
            throw error;
          }
        }
      }

      // Trigger cart update event for other components
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: cartData }
      }));
      
      // Reset button state after successful completion
      setTimeout(() => {
        // Find the button again in case the DOM was re-rendered
        const currentButton = this.container?.querySelector(`[data-variant-id="${variantId}"]`);
        if (currentButton && currentButton.textContent === window.translation.adding) {
          currentButton.textContent = window.translation.addToCart;
          currentButton.disabled = false;
        }
      }, 100);

    } catch (error) {
      console.error('CartDrawerRecommendations: Error in addToCart', {
        error: error.message,
        stack: error.stack,
        variantId,
        productId
      });
      
      button.textContent = 'Error';
      setTimeout(() => {
        button.textContent = window.translation.addToCart;
        button.disabled = false;
      }, 2000);
    }
  }

  showLoading() {
    if (this.loadingContainer) {
      this.loadingContainer.style.display = 'flex';
    }
    this.hideProducts();
    this.hideFallback();
  }

  hideLoading() {
    if (this.loadingContainer) {
      this.loadingContainer.style.display = 'none';
    }
  }

  showProducts() {
    if (this.productsContainer) {
      this.productsContainer.style.display = 'block';
    }
    // Ensure main container is visible and remove any inline display:none
    if (this.container) {
      this.container.style.display = 'block';
      this.container.style.removeProperty('display'); // Remove any inline display styles
    }
    this.updateArrowState();
  }

  updateArrowState() {
    if (!this.container) return;
    const list = this.container.querySelector('.cart-drawer-recommendations-list');
    const prevBtn = this.container.querySelector('.cart-drawer-recommendations__arrow--prev');
    const nextBtn = this.container.querySelector('.cart-drawer-recommendations__arrow--next');

    if (!list || (!prevBtn && !nextBtn)) return;

    const maxScroll = Math.max(0, list.scrollWidth - list.clientWidth);
    const currentScroll = Math.abs(list.scrollLeft);
    const isAtStart = currentScroll <= 5;
    const isAtEnd = currentScroll >= maxScroll - 5;

    if (prevBtn) prevBtn.disabled = isAtStart;
    if (nextBtn) nextBtn.disabled = isAtEnd;
  }

  hideProducts() {
    if (this.productsContainer) {
      this.productsContainer.style.display = 'none';
    }
  }

  showFallback() {
    if (this.fallbackContainer) {
      this.fallbackContainer.style.display = 'block';
      this.initFallbackSwiper();
    }
    this.hideLoading();
    this.hideProducts();
    // Ensure main container is visible and remove any inline display:none
    if (this.container) {
      this.container.style.display = 'block';
      this.container.style.removeProperty('display'); // Remove any inline display styles
    }
  }

  initFallbackSwiper() {
    if (!this.fallbackContainer) return;
    const swiperEl = this.fallbackContainer.querySelector('.cart-drawer-recommendations-swiper');
    if (!swiperEl || swiperEl._swiperInstance) return;

    const SwiperClass = window.Swiper || window.swiper;
    if (typeof SwiperClass === 'function') {
      try {
        swiperEl._swiperInstance = new SwiperClass(swiperEl, {
          slidesPerView: 'auto',
          spaceBetween: 13,
          navigation: {
            nextEl: swiperEl.querySelector('.cart-drawer-recommendations-next') || '.cart-drawer-recommendations-next',
            prevEl: swiperEl.querySelector('.cart-drawer-recommendations-prev') || '.cart-drawer-recommendations-prev',
          },
          grabCursor: true,
        });
      } catch (e) {
        // fail silently
      }
    }
  }

  hideFallback() {
    if (this.fallbackContainer) {
      this.fallbackContainer.style.display = 'none';
    }
  }

  hideRecommendations() {
    this.container.style.display = 'none';
  }

  updateCartProductIds(cartItems) {
    let productIds = '';
    
    // Handle different data structures
    if (Array.isArray(cartItems)) {
      // Standard cart items array
      productIds = cartItems.map(item => {
        return item.product_id;
      }).join(',');
    } else if (cartItems && typeof cartItems === 'object') {
      // Single item object (like from cart-drawer:rendered event)
      if (cartItems.product_id) {
        productIds = cartItems.product_id.toString();
      } else if (cartItems.items && Array.isArray(cartItems.items)) {
        // Cart object with items array
        productIds = cartItems.items.map(item => item.product_id).join(',');
      } else {
        // If no valid cart items, hide recommendations
        if (this.container) {
          this.hideRecommendations();
        }
        this.lastCartProductIds = '';
        return;
      }
    } else {
      // If no valid cart items, hide recommendations
      if (this.container) {
        this.hideRecommendations();
      }
      this.lastCartProductIds = '';
      return;
    }
    
    // Update the tracked product IDs
    this.lastCartProductIds = productIds;
    
    // Try to find the container (it might not exist if cart drawer is closed)
    this.container = document.querySelector('.cart-drawer-recommendations');
    
    if (this.container) {
      this.container.dataset.cartProductIds = productIds;
      
      if (productIds && productIds.trim() !== '') {
        // Show recommendations container and ensure it's visible
        this.ensureContainerVisible();
        
        // Only reload recommendations if it's not a quantity-only change
        if (!this.isQuantityOnlyChange) {
          // Reset the loaded flag to allow reloading
          this.hasLoadedRecommendations = false;
          // Fetch fresh cross-sell handles (cart composition changed) then reload
          if (!this.isLoading) {
            this.refreshCrossSellHandlesAndLoad();
          }
        } else {
          // Don't reset the flag here - let it be reset in reinitializeListeners
        }
      } else {
        this.hideRecommendations();
      }
    } else {
      // Store the product IDs so they can be used when the cart drawer opens
      this.pendingCartProductIds = productIds;
    }
  }

  /**
   * Fetches fresh section HTML to extract an up-to-date data-cross-sell-handles
   * value, then updates the container's dataset so that the subsequent
   * loadRecommendations() call uses correct cross-sell products for the new
   * cart composition (including all products, not just the first one).
   */
  async refreshCrossSellHandlesAndLoad() {
    await this.refreshCrossSellHandles();
    if (!this.isLoading) {
      this.loadRecommendations();
    }
  }

  async refreshCrossSellHandles() {
    if (!this.container) return;
    try {
      const rootUrl = (window.routes && window.routes.root_url && window.routes.root_url !== '/') ? window.routes.root_url : '';
      const response = await fetch(`${rootUrl}/?sections=cart-drawer`);
      if (!response.ok) return;

      const data = await response.json();
      const sectionHtml = data['cart-drawer'];
      if (!sectionHtml) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHtml, 'text/html');
      const freshContainer = doc.querySelector('.cart-drawer-recommendations');

      if (freshContainer && this.container) {
        // Update both cross-sell handles and cart product IDs from the fresh render
        const freshCrossSellHandles = freshContainer.dataset.crossSellHandles || '';
        const freshCartProductIds = freshContainer.dataset.cartProductIds || '';

        this.container.dataset.crossSellHandles = freshCrossSellHandles;
        if (freshCartProductIds) {
          this.container.dataset.cartProductIds = freshCartProductIds;
        }
      }
    } catch {
      // Fail silently – loadRecommendations will use whatever handles are currently set
    }
  }

  getRecommendationsLimit() {
    // Get the limit from Shopify settings or default to 4
    const limitElement = document.querySelector('[data-cart-recommendations-limit]');
    if (limitElement) {
      return parseInt(limitElement.dataset.cartRecommendationsLimit) || 4;
    }
    return 4;
  }

  isRecommendationsVisible() {
    if (!this.container) return false;
    
    const listContainer = this.container.querySelector('.cart-drawer-recommendations-list');
    const hasListContainer = listContainer && listContainer.querySelectorAll('.cart-drawer-recommendations-item').length > 0;
    
    return (this.productsContainer?.style.display !== 'none' || 
            this.fallbackContainer?.style.display !== 'none') && hasListContainer;
  }

  ensureContainerVisible() {
    if (this.container && this.container.dataset.cartProductIds && this.container.dataset.cartProductIds.trim() !== '') {
      this.container.style.display = 'block';
      this.container.style.removeProperty('display'); // Remove any inline display styles
      
      // Double-check after a brief delay to ensure it's visible
      setTimeout(() => {
        if (this.container && this.container.style.display === 'none') {
          this.container.style.display = 'block';
        }
      }, 0);
    }
  }

  addCartButtonListeners() {
    if (!this.container) return;
    
    // Initialize desktop mouse drag-to-scroll on the recommendations list
    this.initDragToScroll();
    this.updateArrowState();
    
    // Add to Cart forms
    const addForms = this.container.querySelectorAll('.cart-drawer-product-form');
    if (!this.handleFormSubmitBound) {
      this.handleFormSubmitBound = this.handleFormSubmit.bind(this);
    }
    addForms.forEach(form => {
      form.removeEventListener('submit', this.handleFormSubmitBound);
      form.addEventListener('submit', this.handleFormSubmitBound);
    });

    // Add variant radio button listeners
    const variantRadios = this.container.querySelectorAll('.cart-drawer-variant-radios input[type="radio"]');
    variantRadios.forEach(radio => {
      radio.removeEventListener('change', this.handleVariantChangeBound);
      radio.addEventListener('change', this.handleVariantChangeBound);
    });

    // Add variant select listeners
    const variantSelects = this.container.querySelectorAll('.cart-drawer-variant-select');
    variantSelects.forEach(select => {
      select.removeEventListener('change', this.handleVariantChangeBound);
      select.addEventListener('change', this.handleVariantChangeBound);
    });
    
    // Add click listeners to variant wrappers for better UX
    const variantWrappers = this.container.querySelectorAll('.cart-drawer-variant-wrapper');
    variantWrappers.forEach(wrapper => {
      // Remove existing click handler if it exists
      if (wrapper._clickHandler) {
        wrapper.removeEventListener('click', wrapper._clickHandler);
      }
      
      // Create and store the click handler
      wrapper._clickHandler = (e) => {
        // Prevent if clicking on the input itself or label
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'LABEL') {
          return;
        }
        
        const radio = wrapper.querySelector('input[type="radio"]');
        if (radio && !wrapper.classList.contains('unavailable')) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };
      
      wrapper.style.cursor = 'pointer';
      wrapper.addEventListener('click', wrapper._clickHandler);
    });
  }

  // Set up global mouse drag-to-scroll for any .cart-drawer-recommendations-list in the DOM
  static setupGlobalDragToScroll() {
    if (window._cartRecommendationsDragSetup) return;
    window._cartRecommendationsDragSetup = true;

    let isDown = false;
    let startX = 0;
    let scrollStart = 0;
    let isDragging = false;
    let activeList = null;
    let preventClick = false;
    let lastX = 0;
    let lastTime = 0;
    let velocity = 0;
    let rafId = null;

    const stopMomentum = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    document.addEventListener('mousedown', (e) => {
      // Only handle left click (button 0)
      if (e.button !== 0) return;
      const list = e.target.closest('.cart-drawer-recommendations-list');
      if (!list) return;

      stopMomentum();
      isDown = true;
      activeList = list;
      isDragging = false;
      startX = e.clientX;
      scrollStart = list.scrollLeft;
      lastX = e.clientX;
      lastTime = Date.now();
      velocity = 0;
    }, { capture: true, passive: true });

    document.addEventListener('mousemove', (e) => {
      if (!isDown || !activeList) return;

      const currentX = e.clientX;
      const deltaX = currentX - startX;

      if (!isDragging && Math.abs(deltaX) > 4) {
        isDragging = true;
        activeList.classList.add('is-dragging');
      }

      if (isDragging) {
        e.preventDefault();

        const now = Date.now();
        const dt = now - lastTime;
        if (dt > 0) {
          const currentVelocity = (currentX - lastX) / dt;
          velocity = 0.7 * currentVelocity + 0.3 * velocity;
          lastX = currentX;
          lastTime = now;
        }

        const isRTL = getComputedStyle(activeList).direction === 'rtl' || Boolean(activeList.closest('[dir="rtl"]'));
        if (isRTL) {
          activeList.scrollLeft = scrollStart + deltaX;
        } else {
          activeList.scrollLeft = scrollStart - deltaX;
        }
      }
    }, { passive: false });

    const handleMouseUp = () => {
      if (!isDown || !activeList) return;

      const list = activeList;
      isDown = false;
      activeList = null;

      if (isDragging) {
        list.classList.remove('is-dragging');
        preventClick = true;
        setTimeout(() => {
          preventClick = false;
        }, 100);

        if (Math.abs(velocity) > 0.15) {
          const isRTL = getComputedStyle(list).direction === 'rtl' || Boolean(list.closest('[dir="rtl"]'));
          let currentVelocity = velocity * 15;
          const friction = 0.92;
          const step = () => {
            if (Math.abs(currentVelocity) > 0.5) {
              if (isRTL) {
                list.scrollLeft += currentVelocity;
              } else {
                list.scrollLeft -= currentVelocity;
              }
              currentVelocity *= friction;
              rafId = requestAnimationFrame(step);
            } else {
              rafId = null;
            }
          };
          rafId = requestAnimationFrame(step);
        }
      }
    };

    document.addEventListener('mouseup', handleMouseUp, { capture: true });
    window.addEventListener('mouseup', handleMouseUp, { capture: true });

    // Capture-phase click listener to suppress clicks after dragging
    document.addEventListener('click', (e) => {
      if (preventClick && e.target.closest('.cart-drawer-recommendations-list')) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    }, true);

    // Prevent native image and link ghost drag
    document.addEventListener('dragstart', (e) => {
      if (e.target.closest('.cart-drawer-recommendations-list')) {
        e.preventDefault();
      }
    }, true);

    // Navigation arrows click handler
    document.addEventListener('click', (e) => {
      const prevBtn = e.target.closest('.cart-drawer-recommendations__arrow--prev');
      const nextBtn = e.target.closest('.cart-drawer-recommendations__arrow--next');
      if (!prevBtn && !nextBtn) return;

      e.preventDefault();
      e.stopPropagation();

      const container = (prevBtn || nextBtn).closest('.cart-drawer-recommendations');
      if (!container) return;

      const list = container.querySelector('.cart-drawer-recommendations-list');
      const fallbackSwiperEl = container.querySelector('.cart-drawer-recommendations-swiper');

      if (list) {
        const item = list.querySelector('.cart-drawer-recommendations-item');
        const scrollStep = item ? (item.offsetWidth + 13) : 240;
        const isRTL = getComputedStyle(list).direction === 'rtl' || Boolean(list.closest('[dir="rtl"]'));
        let scrollAmount = prevBtn ? -scrollStep : scrollStep;
        if (isRTL) {
          scrollAmount = -scrollAmount;
        }
        list.scrollBy({
          left: scrollAmount,
          behavior: 'smooth',
        });
      } else if (fallbackSwiperEl && fallbackSwiperEl._swiperInstance) {
        if (prevBtn) {
          fallbackSwiperEl._swiperInstance.slidePrev();
        } else {
          fallbackSwiperEl._swiperInstance.slideNext();
        }
      }
    });

    // Update navigation arrows state on scroll
    document.addEventListener('scroll', (e) => {
      const list = e.target;
      if (!list || !list.classList || !list.classList.contains('cart-drawer-recommendations-list')) return;
      const container = list.closest('.cart-drawer-recommendations');
      if (!container) return;

      const prevBtn = container.querySelector('.cart-drawer-recommendations__arrow--prev');
      const nextBtn = container.querySelector('.cart-drawer-recommendations__arrow--next');

      if (!prevBtn && !nextBtn) return;

      const maxScroll = Math.max(0, list.scrollWidth - list.clientWidth);
      const currentScroll = Math.abs(list.scrollLeft);
      const isAtStart = currentScroll <= 5;
      const isAtEnd = currentScroll >= maxScroll - 5;

      if (prevBtn) prevBtn.disabled = isAtStart;
      if (nextBtn) nextBtn.disabled = isAtEnd;
    }, { capture: true, passive: true });
  }

  // Initialize mouse drag-to-scroll (swiping) for desktop
  initDragToScroll() {
    CartDrawerRecommendations.setupGlobalDragToScroll();
  }

  handleFormSubmit(event) {
    event.preventDefault();
    event.stopPropagation(); // Prevent event from bubbling up
    
    const form = event.currentTarget;
    
    // Only handle forms from cart drawer recommendations
    if (!form.closest('.cart-drawer-recommendations')) {
      return;
    }
    
    this.addToCartViaForm(form);
  }

  handleVariantChange(event) {
    const input = event.currentTarget;
    const productId = input.dataset.productId;
    const optionIndex = parseInt(input.dataset.optionIndex);
    const selectedValue = input.value;
    
    // Update active state on variant wrappers if it's a radio
    if (input.tagName.toLowerCase() === 'input' && input.type === 'radio') {
      const variantRadiosContainer = input.closest('.cart-drawer-variant-radios');
      if (variantRadiosContainer) {
        const allWrappers = variantRadiosContainer.querySelectorAll('.cart-drawer-variant-wrapper');
        allWrappers.forEach(wrapper => {
          wrapper.classList.remove('active');
        });
        const selectedWrapper = input.closest('.cart-drawer-variant-wrapper');
        if (selectedWrapper) {
          selectedWrapper.classList.add('active');
        }
      }
    }
    
    // Find the product card containing this selector
    const productCard = input.closest('.cart-drawer-product-card');
    if (!productCard) return;
    
    // Find the form and update the variant ID
    const form = productCard.querySelector('.cart-drawer-product-form');
    if (!form) return;
    
    // Find the variant that matches the selected options
    const currentOptions = this.getCurrentVariantOptions(productCard);
    const matchingVariant = this.findMatchingVariant(productId, currentOptions);
    
    if (matchingVariant) {
      // Update the form's variant ID
      const variantIdInput = form.querySelector('input[name="id"]');
      if (variantIdInput) {
        variantIdInput.value = matchingVariant.id;
      }
      
      // Update the price display
      this.updateProductPrice(productCard, matchingVariant);
      
      // Update button availability
      this.updateButtonAvailability(productCard, matchingVariant);
    }
  }

  // Get current variant options from all radio buttons in a product card
  getCurrentVariantOptions(productCard) {
    const options = [];
    const radios = productCard.querySelectorAll('.cart-drawer-variant-radios input[type="radio"]:checked');
    
    radios.forEach(radio => {
      const optionIndex = parseInt(radio.dataset.optionIndex);
      options[optionIndex] = radio.value;
    });

    const selects = productCard.querySelectorAll('.cart-drawer-variant-select');
    
    selects.forEach(select => {
      const optionIndex = parseInt(select.dataset.optionIndex);
      options[optionIndex] = select.value;
    });
    
    return options;
  }

  // Find variant that matches the selected options
  findMatchingVariant(productId, selectedOptions) {
    // Find the product in the current recommendations
    const product = this.currentRecommendations?.find(p => p.id.toString() === productId);
    if (!product) return null;
    
    // Find the variant that matches all selected options
    const matchingVariant = product.variants.find(variant => {
      return selectedOptions.every((option, index) => 
        option === undefined || variant.options[index] === option
      );
    });
    
    return matchingVariant || product.variants[0];
  }

  // Update product price when variant changes
  updateProductPrice(productCard, variant) {
    const priceContainer = productCard.querySelector('.cart-drawer-product-card__price');
    if (priceContainer) {
      priceContainer.innerHTML = this.formatPrice(variant.price, variant.compare_at_price);
    }
  }

  // Update button availability when variant changes
  updateButtonAvailability(productCard, variant) {
    const button = productCard.querySelector('.cart-drawer-product-card__add');
    if (button) {
      button.disabled = !variant.available;
      // button.textContent = variant.available ? window.translation.addToCart : window.translation.soldOut;
    }
  }

  preserveState() {
    if (this.container) {
      const listContainer = this.container.querySelector('.cart-drawer-recommendations-list');
      const hasListContainer = listContainer && listContainer.querySelectorAll('.cart-drawer-recommendations-item').length > 0;
      
      this.preservedState = {
        cartProductIds: this.container.dataset.cartProductIds,
        lastCartProductIds: this.lastCartProductIds,
        hasLoadedRecommendations: this.hasLoadedRecommendations,
        productsVisible: this.productsContainer?.style.display !== 'none',
        fallbackVisible: this.fallbackContainer?.style.display !== 'none',
        hasListContainer: hasListContainer
      };
    }
  }

  restoreState() {
    if (this.preservedState && this.container) {
      // Restore the cart product IDs
      this.container.dataset.cartProductIds = this.preservedState.cartProductIds;
      this.lastCartProductIds = this.preservedState.lastCartProductIds || '';
      
      // Restore the loaded state
      this.hasLoadedRecommendations = this.preservedState.hasLoadedRecommendations;
      
      // Only restore visibility if there are cart items
      if (this.preservedState.cartProductIds && this.preservedState.cartProductIds.trim() !== '') {
        // Show recommendations container since cart has items
        this.container.style.display = 'block';
        
        if (this.preservedState.productsVisible && this.preservedState.hasListContainer) {
          this.showProducts();
        } else if (this.preservedState.fallbackVisible) {
          this.showFallback();
        } else {
          // If products were visible but list container is missing, reload recommendations
          this.hasLoadedRecommendations = false;
          setTimeout(() => {
            if (!this.isLoading) {
              this.loadRecommendations();
            }
          }, 100);
        }
      } else {
        // If no cart items, always hide recommendations regardless of preserved state
        this.hideRecommendations();
        this.hasLoadedRecommendations = false; // Reset to allow reloading when items are added
      }
      
      // Clear preserved state
      this.preservedState = null;
    }
  }

  reinitialize() {
    // Preserve current state before re-initialization
    this.preserveState();
    
    // Reset initialization flag to allow re-initialization
    this.isInitialized = false;
    
    // Re-find container elements in case they were re-rendered
    this.container = document.querySelector('.cart-drawer-recommendations');
    this.loadingContainer = this.container?.querySelector('.cart-drawer-recommendations__loading');
    this.productsContainer = this.container?.querySelector('.cart-drawer-recommendations__products');
    this.fallbackContainer = this.container?.querySelector('.cart-drawer-recommendations__fallback');
    
    if (this.container) {
      // Restore preserved state
      this.restoreState();
      
      // Check if cart has items and load recommendations accordingly
      const cartProductIds = this.container.dataset.cartProductIds;
      
      if (cartProductIds && cartProductIds.trim() !== '') {
        // Initialize the last cart product IDs if not already set
        if (!this.lastCartProductIds) {
          this.lastCartProductIds = cartProductIds;
        }
        
        // Show recommendations container since cart has items
        this.container.style.display = 'block';
        
        // Check if recommendations are already visible and properly rendered
        const isAlreadyVisible = this.isRecommendationsVisible();
        
        if (isAlreadyVisible) {
          // Just reinitialize event listeners if recommendations are already visible
          setTimeout(() => {
            this.addCartButtonListeners();
          }, 100);
        } else {
          // Reset the loaded flag to allow reloading
          this.hasLoadedRecommendations = false;
          // Small delay to ensure DOM is fully updated
          setTimeout(() => {
            // Only load if not already loading
            if (!this.isLoading) {
              this.loadRecommendations();
            }
          }, 100);
        }
      } else {
        this.hideRecommendations();
      }
    }
  }

  // Method to re-initialize just event listeners without reloading recommendations
  reinitializeListeners() {
    // Re-find container elements in case they were re-rendered
    this.container = document.querySelector('.cart-drawer-recommendations');
    this.loadingContainer = this.container?.querySelector('.cart-drawer-recommendations__loading');
    this.productsContainer = this.container?.querySelector('.cart-drawer-recommendations__products');
    this.fallbackContainer = this.container?.querySelector('.cart-drawer-recommendations__fallback');
    
    if (this.container) {
      // Check if recommendations are visible and properly rendered
      const listContainer = this.container.querySelector('.cart-drawer-recommendations-list');
      const hasListContainer = listContainer && listContainer.querySelectorAll('.cart-drawer-recommendations-item').length > 0;
      const isVisible = (this.productsContainer?.style.display !== 'none' || 
                       this.fallbackContainer?.style.display !== 'none') && hasListContainer;
      
      if (isVisible) {
        setTimeout(() => {
          this.addCartButtonListeners();
        }, 100);
        // Reset the quantity-only change flag after reinitializing
        this.isQuantityOnlyChange = false;
      } else if (this.isQuantityOnlyChange) {
        // Don't reload recommendations for quantity changes
        this.isQuantityOnlyChange = false; // Reset the flag
      }
    }
  }

  // Method to reset any stuck buttons after cart updates
  resetStuckButtons() {
    if (!this.container) return;
    
    // Reset stuck form buttons
    const stuckFormButtons = this.container.querySelectorAll('.cart-drawer-product-form button[type="submit"]');
    stuckFormButtons.forEach(button => {
      if (button.textContent === window.translation.adding && button.disabled) {
        button.textContent = window.translation.addToCart;
        button.disabled = false;
      }
    });
    
    // Also check for any legacy button selectors
    const stuckButtons = this.container.querySelectorAll('.cart-drawer-product-card__add');
    stuckButtons.forEach(button => {
      if (button.textContent === window.translation.adding && button.disabled) {
        button.textContent = window.translation.addToCart;
        button.disabled = false;
      }
    });
  }

  async addToCartViaForm(form) {
    const variantId = form.querySelector('input[name="id"]').value;
    const submitButton = form.querySelector('button[type="submit"]');
    
    if (!variantId) {
      console.error('CartDrawerRecommendations: No variant ID found in form');
      return;
    }

    // Prevent duplicate submissions
    if (submitButton.disabled && submitButton.textContent === window.translation.adding) {
      return;
    }

    // Save original label so we can restore it (not the global 'Add to Cart' string)
    const originalLabel = submitButton.textContent.trim();

    // Disable button and show loading state
    submitButton.disabled = true;
    submitButton.textContent = window.translation.adding;

    try {
      // Get the cart drawer element
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) {
        console.error('CartDrawerRecommendations: Cart drawer not found');
        throw new Error('Cart drawer not found');
      }

      // Use the same approach as product form
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(form);
      
      // Add sections for cart drawer update (same as product form)
      const sectionsToRender = cartDrawer.getSectionsToRender();
      
      // Convert sections to comma-separated string
      const sectionIds = sectionsToRender.map((section) => section.id).join(',');
      
      formData.append('sections', sectionIds);
      formData.append('sections_url', window.location.pathname);
      cartDrawer.setActiveElement(document.activeElement);
      
      config.body = formData;

      const response = await fetch(`${window.routes.cart_add_url}`, config);

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const cartData = await response.json();
      
      if (cartData.status) {
        // Handle error
        throw new Error(cartData.description || 'Failed to add to cart');
      }

      // Trigger cart update via event system so onCartUpdate() handles it
      // (renderContents is blocked by our guard since add.js sections use wrong keys on product pages)
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-recommendations',
          productVariantId: variantId,
          cartData: cartData,
        });
      }
      // --- GA4 Upsell Tracking ---
      if (typeof gtag !== 'undefined') {
        gtag('event', 'upsell_added', {
          location: 'cart_drawer_widget',
          item_variant_id: variantId,
          item_name: cartData.title || '',
          value: cartData.price ? cartData.price / 100 : 0,
          currency: 'GBP'
        });
      }
      
      // Reset button state after successful completion
      submitButton.textContent = originalLabel;
      submitButton.disabled = false;

    } catch (error) {
      console.error('CartDrawerRecommendations: Error in addToCartViaForm', {
        error: error.message,
        stack: error.stack,
        variantId
      });
      
      submitButton.textContent = 'Error';
      setTimeout(() => {
        submitButton.textContent = originalLabel;
        submitButton.disabled = false;
      }, 2000);
    }
  }

  async checkIfItemInCart(variantId) {
    try {
      // Get current cart state
      const cartJsUrl = (window.routes && window.routes.cart_url ? window.routes.cart_url + '.js' : '/cart.js');
      const response = await fetch(cartJsUrl);
      if (!response.ok) {
        console.error('CartDrawerRecommendations: Failed to fetch cart state');
        return false;
      }
      
      const cart = await response.json();
      
      // Check if variant is already in cart
      const isInCart = cart.items?.some(item => item.variant_id.toString() === variantId.toString());
      
      return isInCart;
    } catch (error) {
      console.error('CartDrawerRecommendations: Error checking cart state', error);
      return false;
    }
  }

  async updateItemQuantity(variantId, quantity) {
    try {
      // Get current cart state
      const cartJsUrl = (window.routes && window.routes.cart_url ? window.routes.cart_url + '.js' : '/cart.js');
      const response = await fetch(cartJsUrl);
      if (!response.ok) {
        console.error('CartDrawerRecommendations: Failed to fetch cart state');
        return;
      }
      
      const cart = await response.json();
      
      // Find the item to update
      const itemToUpdate = cart.items?.find(item => item.variant_id.toString() === variantId.toString());
      if (!itemToUpdate) {
        console.error('CartDrawerRecommendations: Item not found in cart');
        return;
      }
      
      // Update the quantity
      itemToUpdate.quantity = quantity;
      
      // Send the updated cart state back to Shopify
      const cartUpdateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
      const updateResponse = await fetch(cartUpdateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          updates: {
            [variantId]: quantity
          }
        })
      });

      if (!updateResponse.ok) {
        console.error('CartDrawerRecommendations: Failed to update cart');
        return;
      }

      const updatedCart = await updateResponse.json();
      
      // Trigger cart update event for other components
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: updatedCart }
      }));

    } catch (error) {
      console.error('CartDrawerRecommendations: Error updating item quantity', error);
    }
  }
}

// Set up global drag-to-scroll immediately
CartDrawerRecommendations.setupGlobalDragToScroll();

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    CartDrawerRecommendations.setupGlobalDragToScroll();
    new CartDrawerRecommendations();
  });
} else {
  new CartDrawerRecommendations();
}

// Export for use in other scripts
window.CartDrawerRecommendations = CartDrawerRecommendations; 